import { toast as showToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AccessCode, AccessInfo } from './types';

export const verifyAccessCode = async (
  code: string,
  toast: typeof showToast,
  onSuccess?: () => Promise<void>
): Promise<[boolean, AccessInfo | null]> => {
  try {
    // Check if the code exists in the database
    const { data: accessCode, error } = await supabase
      .from('access_codes')
      .select('*')
      .eq('code', code)
      .single();

    if (error || !accessCode) {
      toast({
        title: 'Invalid access code',
        description: 'The access code you entered is not valid',
        variant: 'destructive',
      });
      return [false, null];
    }

    // If it's a client-specific code, get the client name
    let clientName = null;
    if (accessCode.client_id) {
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('name')
        .eq('id', accessCode.client_id)
        .single();

      if (clientError || !client) {
        toast({
          title: 'Error',
          description: 'Failed to retrieve client information',
          variant: 'destructive',
        });
        return [false, null];
      }

      clientName = client.name;
    }

    // Create access info object
    const accessInfo: AccessInfo = {
      isAdmin: accessCode.is_admin,
      clientId: accessCode.client_id,
      clientName: clientName,
      userId: accessCode.id, // Add the user ID from the access code
    };

    toast({
      title: 'Access granted',
      description: accessCode.is_admin
        ? 'You now have admin access'
        : `You now have access to ${clientName || 'the system'}`,
    });

    // Call the onSuccess callback if provided
    if (onSuccess) {
      await onSuccess();
    }

    return [true, accessInfo];
  } catch (error) {
    console.error('Error verifying access code:', error);
    toast({
      title: 'Error',
      description: 'An unexpected error occurred',
      variant: 'destructive',
    });
    return [false, null];
  }
};

export const createAccessCode = async (
  clientId: string | null,
  isAdmin: boolean,
  toast: typeof showToast
): Promise<string | null> => {
  try {
    // Generate a random 8-character code
    const code = Math.random().toString(36).substring(2, 10).toUpperCase();

    // Insert the new access code into the database
    const { data, error } = await supabase
      .from('access_codes')
      .insert([
        {
          code,
          client_id: clientId,
          is_admin: isAdmin,
        },
      ])
      .select()
      .single();

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to create access code',
        variant: 'destructive',
      });
      return null;
    }

    toast({
      title: 'Access code created',
      description: 'The access code has been created successfully',
    });

    return code;
  } catch (error) {
    console.error('Error creating access code:', error);
    toast({
      title: 'Error',
      description: 'An unexpected error occurred',
      variant: 'destructive',
    });
    return null;
  }
};
